import { z } from 'zod';
/* ==========================================================================
 *  Pricing & unit-economics calculator (ADR-0011 §3 + §4.4)
 *
 *  The locked, pure, IO-free contract the admin pricing-decision view (Phase D)
 *  renders. It makes the static "Unit economics" model in
 *  docs/09-metrics-and-monetization.md *live*: feed it real actuals (segmented
 *  AI cost, infra/AU, conversion, store fees, current price) and it answers the
 *  portal's reason to exist — "what should we charge, and does €7.99 lose money?"
 *
 *  Everything here is a pure function over plain numbers. No DB, no Fastify, no
 *  IO, no secrets — @nutians/shared is bundled into the browser, and the
 *  what-if sliders recompute these client-side over a server-provided baseline
 *  (ADR-0011 §2.2). The accompanying zod schemas describe the renderable output
 *  shapes (the contract D2 renders); the functions produce values matching them.
 *
 *  ── The core identity (ADR-0011 §3.1) ──────────────────────────────────────
 *    blended_cost_per_AU  = 0.30·light + 0.50·avg + 0.20·heavy        (§4.4)
 *    cost_per_paying_user = (blended_cost_per_AU + infra_per_AU) / conversion
 *
 *  ── Store-fee gross-up (ADR-0011 §3.2) ─────────────────────────────────────
 *    net_factor            = (1 − store_fee) · (1 − rc_fee)
 *    break_even_list_price = cost_per_paying_user / net_factor
 *    target_list_price(M)  = cost_per_paying_user / (net_factor · (1 − M))
 *    margin_at(list_price) = 1 − cost_per_paying_user / (list_price · net_factor)
 *
 *  ── Reconciliation with the §3.3 worked example ────────────────────────────
 *  FORMULA NOTE / flagged ambiguity: ADR-0011 §3.2 defines break-even and the
 *  target price WITH the store-fee gross-up (÷ net_factor), and §3.3's banner
 *  prose says "Apple 30% cut". But the banner's quoted numbers
 *  (break-even €14.07, 80%-margin €70.34, margin −76%, loss €6.08 at €7.99)
 *  reconcile ONLY when net_factor = 1.0 — i.e. NO store fee is actually applied,
 *  with margin measured on the GROSS list price:
 *      cost_per_paying_user = (0.41 + 0.18) / 0.042            ≈ €14.05
 *      break_even   = cost / 1.0                                ≈ €14.07
 *      target(0.80) = cost / (1.0 · 0.20)                       ≈ €70.34
 *      margin(7.99) = 1 − cost / (7.99 · 1.0)                   ≈ −76%
 *      loss/payer   = cost − 7.99 · 1.0                         ≈ €6.08
 *  So the banner's PROSE ("30% cut") and its NUMBERS disagree. We implement the
 *  §3.2 gross-up formulas faithfully (net_factor is a first-class input), and
 *  the §3.3 numbers fall out exactly as the net_factor = 1 special case
 *  (storeFee = 0, rcFee = 0). Callers that want the post-store-fee break-even
 *  pass the real fees; the sensitivity grid's cells are "post store-fee" per
 *  §3.3 #2 and take net_factor explicitly. This is called out so a reviewer can
 *  confirm intent rather than have it silently guessed.
 * ========================================================================== */
/* ----- Usage segmentation: the 50/20/30 model (ADR-0011 §4.4) -------------- */
/**
 * Population split for the cost bands, as fractions that MUST sum to 1.
 * Defaults to the spec's Light = bottom 30% / Average = middle 50% /
 * Heavy = top 20% (ADR-0011 §4.4; "bands configurable").
 */
export const costBandConfigSchema = z
    .object({
    light: z.number().gt(0).lt(1),
    average: z.number().gt(0).lt(1),
    heavy: z.number().gt(0).lt(1),
})
    .refine((b) => Math.abs(b.light + b.average + b.heavy - 1) < 1e-9, {
    message: 'band fractions (light + average + heavy) must sum to 1',
});
/** Canonical 50/20/30 bands from ADR-0011 §4.4. */
export const DEFAULT_COST_BANDS = {
    light: 0.3,
    average: 0.5,
    heavy: 0.2,
};
export const costBandLabelSchema = z.enum(['light', 'average', 'heavy']);
/** One percentile band: its population weight, headcount, and mean cost/AU. */
export const costBandSchema = z.object({
    label: costBandLabelSchema,
    /** Configured population fraction for this band (0.30 / 0.50 / 0.20). */
    weight: z.number().nonnegative(),
    /** Number of active users that fell into this band. */
    count: z.number().int().nonnegative(),
    /** Mean trailing-window AI cost/AU within the band; null when the band is empty. */
    meanCostEur: z.number().nonnegative().nullable(),
});
/** Output of {@link segmentCostBands}: the three bands plus the total headcount. */
export const segmentedCostBandsSchema = z.object({
    light: costBandSchema,
    average: costBandSchema,
    heavy: costBandSchema,
    totalCount: z.number().int().nonnegative(),
});
/* ----- Sensitivity grid (ADR-0011 §3.3 #2) -------------------------------- */
/** One cell of the sensitivity table: a (conversion × blended-cost) break-even. */
export const sensitivityCellSchema = z.object({
    conversionRate: z.number().nonnegative(),
    blendedCostPerAu: z.number().nonnegative(),
    /** Post-store-fee break-even list price; null when conversion = 0 (no payers). */
    breakEvenListPrice: z.number().nonnegative().nullable(),
    /** Green (true) when break-even ≤ current price; red (false) when it exceeds it. */
    passesAtCurrentPrice: z.boolean(),
});
/** Output of {@link sensitivityGrid}: rows = conversions, columns = blended costs. */
export const sensitivityGridSchema = z.object({
    /** Row axis values (e.g. 0.02, 0.04, 0.06, 0.08, 0.10). */
    conversions: z.array(z.number().nonnegative()),
    /** Column axis values (e.g. 0.30, 0.50, 0.70). */
    blendedCosts: z.array(z.number().nonnegative()),
    /** Current list price the cells are coloured against (€7.99 today). */
    currentPrice: z.number().nonnegative(),
    /** Fixed infra/AU added to every cell's COGS. */
    infraPerAu: z.number().nonnegative(),
    /** Store-fee gross-up factor applied to every cell. */
    netFactor: z.number(),
    /** cells[rowIndex][colIndex] — indexed by conversion, then blended cost. */
    cells: z.array(z.array(sensitivityCellSchema)),
});
/* ----- Store-fee blend (recommended default for the verdict) -------------- */
/**
 * Named store-fee tiers. The store cut is a *real* cost (ADR-0011 §3.2 — "the
 * silent margin killer"), so the verdict must be computed net of it. At pilot
 * scale (<$1M/yr billings) both Apple and Google charge the reduced
 * small-business rate, so {@link DEFAULT_STORE_FEE} is 15%, not 30%; the 30%
 * tier is the "if we scale past $1M" stress case.
 */
export const STORE_FEE = {
    appleStandard: 0.3,
    appleSmallBusiness: 0.15,
    googleStandard: 0.3,
    googleReduced: 0.15,
    /** Web checkout via Stripe — no app-store cut, ~3% processing. */
    web: 0.03,
};
/** RevenueCat's platform fee (~1%) layered on top of the store cut. */
export const DEFAULT_RC_FEE = 0.01;
/** Pilot-scale default store fee: the small-business tier (<$1M/yr). */
export const DEFAULT_STORE_FEE = STORE_FEE.appleSmallBusiness;
/**
 * One revenue channel in the store mix. `revenueWeight` is gross billings
 * through this channel in any unit (it is renormalised, so raw counts or EUR
 * both work); `storeFee` is that channel's cut (e.g. 0.15 Apple small-biz,
 * 0.30 standard, 0.03 web).
 */
export const storeChannelSchema = z.object({
    /** Channel label — typically a `subscription.store` value or 'web'. */
    store: z.string(),
    /** Gross billings via this channel (any unit; renormalised across channels). */
    revenueWeight: z.number().nonnegative(),
    /** This channel's store fee in [0, 1]. */
    storeFee: z.number().min(0).max(1),
});
/* ----- Pricing verdict (the §3.3 #1 banner, structured) ------------------- */
/** Inputs the verdict banner is computed from — all live actuals + the current price. */
export const pricingVerdictInputSchema = z.object({
    mau: z.number().int().nonnegative(),
    conversionRate: z.number(),
    blendedCostPerAu: z.number().nonnegative(),
    infraPerAu: z.number().nonnegative(),
    /** Net factor to gross up against — pass {@link blendedNetFactor} or {@link netFactor}. */
    netFactor: z.number(),
    currentPrice: z.number().nonnegative(),
    /** Target gross margin (docs/09 §Cost: ≥ 80%). Defaults to 0.80. */
    targetMargin: z.number().default(0.8),
});
/**
 * Structured verdict — the §3.3 #1 "no-doubt" banner as numbers, not prose.
 * D2 formats the sentence (currency, i18n); this module owns the math. Every
 * money field is net of store + RC fees. Null fields propagate the conversion=0
 * / net-factor≤0 guards.
 */
export const pricingVerdictSchema = z.object({
    mau: z.number().int().nonnegative(),
    conversionRate: z.number(),
    blendedCostPerAu: z.number().nonnegative(),
    infraPerAu: z.number().nonnegative(),
    netFactor: z.number(),
    currentPrice: z.number().nonnegative(),
    targetMargin: z.number(),
    /** (blended + infra) / conversion — the true per-paying-user cost floor. */
    costPerPayingUser: z.number().nonnegative().nullable(),
    /** Post-store-fee break-even list price. */
    breakEvenListPrice: z.number().nonnegative().nullable(),
    /** Post-store-fee list price hitting `targetMargin`. */
    targetListPrice: z.number().nonnegative().nullable(),
    /** Gross margin earned at `currentPrice`, net of fees (negative = loss). */
    marginAtCurrentPrice: z.number().nullable(),
    /** What we actually keep per payer at `currentPrice`: currentPrice · netFactor. */
    netRevenuePerPayingUser: z.number().nonnegative(),
    /** netRevenue − cost (negative = loss per paying user, e.g. −€8.51). */
    profitPerPayingUser: z.number().nullable(),
    /** True when margin at current price ≥ targetMargin. */
    meetsTargetMargin: z.boolean(),
    /** True when profit per paying user > 0. */
    isProfitable: z.boolean(),
});
/* ==========================================================================
 *  Pure functions
 * ========================================================================== */
/** Mean of an array, or null when empty (guards every per-band division). */
function meanOrNull(xs) {
    if (xs.length === 0)
        return null;
    let sum = 0;
    for (const x of xs)
        sum += x;
    return sum / xs.length;
}
/** Clamp an integer index into the inclusive range [0, n]. */
function clampIndex(i, n) {
    if (i < 0)
        return 0;
    if (i > n)
        return n;
    return i;
}
/**
 * Bucket per-AU costs into Light / Average / Heavy percentile bands and report
 * the mean cost and headcount of each (ADR-0011 §4.4).
 *
 * Costs are sorted ascending and sliced by CUMULATIVE rounded boundaries
 * (round(n·light), round(n·(light+average))). Cumulative rounding guarantees
 * every user lands in exactly one band and the band counts sum to `n` — no
 * gaps, no double-counting — even when n·fraction is non-integer. With few
 * users a band may be empty (its mean is null), e.g. a single user falls into
 * Average. Empty input yields three empty bands.
 *
 * Output feeds {@link blendedCostPerAu}, seeding the simulator from reality so
 * the model self-calibrates as the pilot accrues data.
 */
export function segmentCostBands(costsPerAu, bands = DEFAULT_COST_BANDS) {
    const n = costsPerAu.length;
    const sorted = [...costsPerAu].sort((a, b) => a - b);
    const lightEnd = clampIndex(Math.round(n * bands.light), n);
    const avgEnd = clampIndex(Math.max(Math.round(n * (bands.light + bands.average)), lightEnd), n);
    const lightSlice = sorted.slice(0, lightEnd);
    const avgSlice = sorted.slice(lightEnd, avgEnd);
    const heavySlice = sorted.slice(avgEnd, n);
    return {
        light: {
            label: 'light',
            weight: bands.light,
            count: lightSlice.length,
            meanCostEur: meanOrNull(lightSlice),
        },
        average: {
            label: 'average',
            weight: bands.average,
            count: avgSlice.length,
            meanCostEur: meanOrNull(avgSlice),
        },
        heavy: {
            label: 'heavy',
            weight: bands.heavy,
            count: heavySlice.length,
            meanCostEur: meanOrNull(heavySlice),
        },
        totalCount: n,
    };
}
/**
 * Blend the band means into a single cost/AU (ADR-0011 §3.1):
 *   blended = 0.30·light + 0.50·avg + 0.20·heavy
 *
 * Implemented as a weighted average over the bands that actually have data,
 * renormalised by the present weights. When all three bands are populated their
 * weights sum to 1, so this is exactly the §3.1 identity. When a band is empty
 * (few users), renormalising over the non-empty bands keeps the blend honest
 * (a lone Average user yields that user's cost, not half of it). Returns 0 when
 * there is no data at all.
 */
export function blendedCostPerAu(seg) {
    const present = [seg.light, seg.average, seg.heavy].filter((b) => b.meanCostEur !== null && b.count > 0);
    let weightSum = 0;
    let weighted = 0;
    for (const b of present) {
        weightSum += b.weight;
        weighted += b.weight * b.meanCostEur;
    }
    if (weightSum === 0)
        return 0;
    return weighted / weightSum;
}
/**
 * The true cost floor — cost per *paying* user, not per active user, because
 * paying users must cover the free riders (ADR-0011 §3.1):
 *   cost_per_paying_user = (blended_cost_per_AU + infra_per_AU) / conversion
 *
 * Returns null when conversionRate ≤ 0 (guard: no payers ⇒ no finite per-payer
 * cost, never a division by zero or Infinity).
 */
export function costPerPayingUser(args) {
    const { blendedCostPerAu: blended, infraPerAu, conversionRate } = args;
    if (conversionRate <= 0)
        return null;
    return (blended + infraPerAu) / conversionRate;
}
/**
 * The fraction of the list price we actually keep after the store and
 * RevenueCat take their cut (ADR-0011 §3.2):
 *   net_factor = (1 − store_fee) · (1 − rc_fee)
 *
 * storeFee = 0 ⇒ keep everything (the §3.3 worked-example case); storeFee = 1 ⇒
 * net_factor = 0 (the store takes all — break-even/target become undefined).
 */
export function netFactor(args) {
    return (1 - args.storeFee) * (1 - args.rcFee);
}
/**
 * The list price at which net revenue exactly covers cost (ADR-0011 §3.2):
 *   break_even_list_price = cost_per_paying_user / net_factor
 *
 * Null when cost is null (conversion = 0) or net_factor ≤ 0 (store takes ≥100%).
 */
export function breakEvenListPrice(args) {
    const { costPerPayingUser: cost, netFactor: nf } = args;
    if (cost === null)
        return null;
    if (nf <= 0)
        return null;
    return cost / nf;
}
/**
 * The list price that yields a target gross margin M (ADR-0011 §3.2):
 *   target_list_price(M) = cost_per_paying_user / (net_factor · (1 − M))
 *
 * Null when cost is null, net_factor ≤ 0, or margin ≥ 1 (an unreachable 100%+
 * margin ⇒ division by ≤ 0). Negative margins are permitted (a deliberately
 * loss-leading price below break-even) and return a finite price.
 */
export function targetListPrice(args) {
    const { costPerPayingUser: cost, netFactor: nf, margin } = args;
    if (cost === null)
        return null;
    if (nf <= 0)
        return null;
    if (margin >= 1)
        return null;
    return cost / (nf * (1 - margin));
}
/**
 * The gross margin earned at a given list price (ADR-0011 §3.2, inverse of
 * {@link targetListPrice}):
 *   margin = (net_revenue − cost) / net_revenue
 *          = 1 − cost_per_paying_user / (list_price · net_factor)
 *
 * Consistent with the other identities: margin = 0 at the break-even price and
 * margin = M at target_list_price(M). At €7.99 against a €14.07 floor it is the
 * portal's "no-doubt" −76% verdict. Null when cost is null or net revenue is 0
 * (list_price = 0 or net_factor = 0). Margin may be negative (a loss).
 */
export function marginAtPrice(args) {
    const { listPrice, costPerPayingUser: cost, netFactor: nf } = args;
    if (cost === null)
        return null;
    const netRevenue = listPrice * nf;
    if (netRevenue <= 0)
        return null;
    return 1 - cost / netRevenue;
}
/**
 * The sensitivity table — the centrepiece artifact (ADR-0011 §3.3 #2).
 * Rows = conversion rates, columns = blended cost/AU. Each cell is the
 * post-store-fee break-even list price, plus a pass/fail flag vs the current
 * price (green when break-even ≤ current price, red when it exceeds it). One
 * glance shows the conversion×cost regions where the current price works.
 *
 * `cells[i][j]` corresponds to `conversions[i]` × `blendedCosts[j]`.
 */
export function sensitivityGrid(conversions, blendedCosts, opts) {
    const { infraPerAu, netFactor: nf, currentPrice } = opts;
    const cells = conversions.map((conversionRate) => blendedCosts.map((blended) => {
        const cost = costPerPayingUser({
            blendedCostPerAu: blended,
            infraPerAu,
            conversionRate,
        });
        const breakEven = breakEvenListPrice({ costPerPayingUser: cost, netFactor: nf });
        return {
            conversionRate,
            blendedCostPerAu: blended,
            breakEvenListPrice: breakEven,
            passesAtCurrentPrice: breakEven !== null && breakEven <= currentPrice,
        };
    }));
    return {
        conversions: [...conversions],
        blendedCosts: [...blendedCosts],
        currentPrice,
        infraPerAu,
        netFactor: nf,
        cells,
    };
}
/**
 * Revenue-weighted net factor across the actual store mix — the recommended
 * input to the verdict instead of a hardcoded single store fee. Generalises
 * {@link netFactor}: a single channel reproduces it exactly.
 *
 *   net_factor = ( Σ revenueWeight·(1 − storeFee) / Σ revenueWeight ) · (1 − rc_fee)
 *
 * `revenueWeight` is renormalised, so channels may carry raw billings or
 * fractions. RC's fee is applied once on top (ADR-0011 §3.2 form). With no
 * channels there is no revenue to take a cut from, so this returns 0 (which
 * makes break-even/target null downstream) — pass at least one channel for a
 * usable baseline; {@link DEFAULT_STORE_FEE} is the suggested default fee.
 */
export function blendedNetFactor(channels, opts = {}) {
    const rcFee = opts.rcFee ?? DEFAULT_RC_FEE;
    let totalWeight = 0;
    let keptWeight = 0;
    for (const c of channels) {
        totalWeight += c.revenueWeight;
        keptWeight += c.revenueWeight * (1 - c.storeFee);
    }
    if (totalWeight <= 0)
        return 0;
    return (keptWeight / totalWeight) * (1 - rcFee);
}
/**
 * Compose the §3.3 #1 verdict banner from live actuals — the portal's
 * "no-doubt" line, as a struct of net-of-fee numbers. Every money figure is
 * post store + RC fee (the recommended convention: the more conservative, truer
 * verdict). D2 renders the sentence; this owns the math. Conversion = 0 (or a
 * net factor ≤ 0) propagates as null cost/break-even/target/margin/profit, with
 * `meetsTargetMargin` and `isProfitable` both false.
 */
export function verdict(input) {
    const { mau, conversionRate, blendedCostPerAu, infraPerAu, netFactor, currentPrice, targetMargin, } = pricingVerdictInputSchema.parse(input);
    const cost = costPerPayingUser({ blendedCostPerAu, infraPerAu, conversionRate });
    const breakEven = breakEvenListPrice({ costPerPayingUser: cost, netFactor });
    const target = targetListPrice({ costPerPayingUser: cost, netFactor, margin: targetMargin });
    const margin = marginAtPrice({ listPrice: currentPrice, costPerPayingUser: cost, netFactor });
    const netRevenuePerPayingUser = currentPrice * netFactor;
    const profitPerPayingUser = cost === null ? null : netRevenuePerPayingUser - cost;
    return {
        mau,
        conversionRate,
        blendedCostPerAu,
        infraPerAu,
        netFactor,
        currentPrice,
        targetMargin,
        costPerPayingUser: cost,
        breakEvenListPrice: breakEven,
        targetListPrice: target,
        marginAtCurrentPrice: margin,
        netRevenuePerPayingUser,
        profitPerPayingUser,
        meetsTargetMargin: margin !== null && margin >= targetMargin,
        isProfitable: profitPerPayingUser !== null && profitPerPayingUser > 0,
    };
}
//# sourceMappingURL=pricing.js.map